import { IconProp, Library, SizeProp } from '@fortawesome/fontawesome-svg-core';
import {
  faFacebook,
  faInstagram,
  faPinterest,
  faTwitter,
  faTwitterSquare,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import {
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faArrowUpFromBracket,
  faBars,
  faBasketShopping,
  faBookOpen,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircleUser,
  faClock,
  faEllipsisVertical,
  faEuroSign,
  faGreaterThan,
  faHeart,
  faHouse,
  faLeaf,
  faLessThan,
  faLocationDot,
  faMagnifyingGlass,
  faPalette,
  faPercent,
  faPlus,
  faSliders,
  faStore,
  faTableList,
  faUser,
  faVideo,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const addIcons = (library: Library) => {
  library.add(
    faArrowLeft,
    faArrowRight,
    faArrowUp,
    faArrowUpFromBracket,
    faBars,
    faBars,
    faBookOpen,
    faBasketShopping,
    faChevronDown,
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faCircleUser,
    faClock,
    faEllipsisVertical,
    faEuroSign,
    faFacebook,
    faFacebook,
    faGreaterThan,
    faHeart,
    faHouse,
    faInstagram,
    faLeaf,
    faLessThan,
    faLocationDot,
    faMagnifyingGlass,
    faPalette,
    faPercent,
    faPinterest,
    faPinterest,
    faPlus,
    faSliders,
    faStore,
    faTableList,
    faTwitter,
    faTwitterSquare,
    faUser,
    faXmark,
    faYoutube,
    faYoutube,
    faVideo,
  );
};

/*
 To avoid client hydration errors we need to use the require import syntax, as storybook won't allow this 
 we check if window is defined so we can still use the import command.
 see: https://github.com/FortAwesome/Font-Awesome/issues/19348
*/
if (typeof window !== 'undefined') {
  import('@fortawesome/fontawesome-svg-core').then(({ library }) => addIcons(library));
} else {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { library } = require('@fortawesome/fontawesome-svg-core');

  addIcons(library);
}

type GenericIconName = keyof typeof ICON_NAMES;
type CustomIconName = keyof typeof CUSTOM_ICONS;

export type IconName = GenericIconName | CustomIconName;

type Props = {
  name: IconName;
  size?: SizeProp;
  colorClass?: string;
  className?: string;
};

export const ICON_NAMES = {
  'angle-down': 'angle-down',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'arrow-up': 'arrow-up',
  'book-open': 'book-open',
  'cart-shopping': 'cart-shopping',
  'chevron-down': 'chevron-down',
  'chevron-left': 'chevron-left',
  'chevron-right': 'chevron-right',
  'chevron-up': 'chevron-up',
  'circle-user': 'circle-user',
  'euro-sign': 'euro-sign',
  'fa-arrow-up-from-bracket': 'fa-solid fa-arrow-up-from-bracket',
  'fa-ellipsis-vertical': 'fa-solid fa-ellipsis-vertical',
  'fa-xmark': 'fa-xmark',
  'greater-than': 'greater-than',
  'less-than': 'less-than',
  'location-dot': 'location-dot',
  'magnifying-glass': 'magnifying-glass',
  'table-list': 'table-list',
  'fa-video': 'fa-video',
  bars: 'bars',
  clock: 'clock',
  envelope: 'fa-envelope fa-light',
  facebook: 'fa-brands fa-facebook',
  heart: 'heart',
  house: 'house',
  instagram: 'fa-brands fa-instagram',
  leaf: 'leaf',
  paintRoller: 'paintRoller',
  palette: 'palette',
  percent: 'percent',
  pinterest: 'fa-brands fa-pinterest',
  phone: 'fa-phone fa-light',
  plus: 'fa-solid fa-plus',
  sliders: 'sliders',
  store: 'store',
  twitter: 'fa-brands fa-twitter',
  user: 'user',
  youtube: 'fa-brands fa-youtube',
} as const;

export const CUSTOM_ICONS = {
  paintRoller: {
    viewBox: '0 0 33 38',
    path: 'M30.4794 4.25H28.0732V1.15625C28.0732 0.584766 27.6134 0.125 27.0419 0.125L1.60442 0.125C1.03293 0.125 0.573166 0.584766 0.573166 1.15625L0.573166 12.8438C0.573166 13.4152 1.03293 13.875 1.60442 13.875L27.0419 13.875C27.6134 13.875 28.0732 13.4152 28.0732 12.8438L28.0732 7H29.4482L29.4482 15.5938L14.3661 15.5938C13.4165 15.5938 12.6474 16.3629 12.6474 17.3125V23.1563L10.7568 23.1562C10.5677 23.1562 10.413 23.3109 10.413 23.5L10.413 33.2969C10.413 33.3441 10.4216 33.3914 10.4388 33.4301C10.4216 33.4988 10.413 33.5676 10.413 33.6406C10.413 35.6344 12.0286 37.25 14.0224 37.25C16.0161 37.25 17.6318 35.6344 17.6318 33.6406C17.6318 33.5676 17.6232 33.4988 17.606 33.4301C17.6232 33.3871 17.6318 33.3441 17.6318 33.2969L17.6318 23.5C17.6318 23.3109 17.4771 23.1563 17.288 23.1563H15.3974V18.3438H30.4794C31.429 18.3438 32.1982 17.5746 32.1982 16.625V5.96875C32.1982 5.01914 31.429 4.25 30.4794 4.25Z',
  },
  paintBucket: {
    viewBox: '0 0 26 26',
    path: 'M11.0702 5.08373C10.2329 3.84609 9.37552 2.80223 8.60222 2.08012C8.21851 1.70382 7.78109 1.38654 7.30425 1.13863C6.90755 0.948507 6.2805 0.763867 5.69185 1.10207C5.02093 1.49146 4.91124 2.25379 4.90575 2.74007C4.90027 3.29582 5.02275 3.95394 5.22202 4.64863C5.58033 5.90455 6.23846 7.44201 7.11413 9.03979L1.12519 15.0287C0.618801 15.5351 0.633426 16.228 0.72666 16.7069C0.827207 17.2243 1.06852 17.791 1.38478 18.3577C2.02097 19.504 3.06666 20.8458 4.35732 22.1383C5.6498 23.4308 6.99164 24.4746 8.13788 25.1108C8.7046 25.4271 9.27132 25.6684 9.78868 25.769C10.2676 25.8604 10.9587 25.8768 11.4669 25.3704L21.7318 15.1055C21.9475 15.5753 21.9 16.0415 21.8232 16.8221C21.7684 17.3559 21.6989 18.0378 21.6898 18.972V19.1968H21.6953C21.7336 19.6536 21.9421 20.0794 22.2795 20.3897C22.6169 20.6999 23.0586 20.8722 23.517 20.8722C23.9754 20.8722 24.417 20.6999 24.7544 20.3897C25.0919 20.0794 25.3004 19.6536 25.3387 19.1968H25.3442V19.0926C25.3459 19.0213 25.3434 18.9499 25.3369 18.8788C25.2363 16.5936 24.0572 13.8734 21.9366 12.4913C21.8229 12.2198 21.6941 11.955 21.5508 11.6979C20.9128 10.5535 19.869 9.21163 18.5765 7.91915C17.284 6.62666 15.9421 5.58098 14.7959 4.94479C14.2292 4.63035 13.6625 4.38904 13.1451 4.28666C12.6661 4.19526 11.9751 4.18063 11.4669 4.68702L11.0702 5.0819V5.08373ZM7.35544 3.41465C8.05196 4.06546 8.89838 5.10018 9.75211 6.39815L8.46511 7.68698C7.76677 6.35793 7.25855 5.12394 6.97885 4.14407C6.8563 3.74344 6.77603 3.33109 6.73936 2.91374C6.90572 3.02526 7.1123 3.18796 7.35544 3.41465ZM8.07572 10.6632C9.06291 12.2207 10.0995 13.5278 11.0135 14.3797C11.1014 14.4616 11.2045 14.5254 11.317 14.5674C11.4295 14.6094 11.5492 14.6288 11.6692 14.6246C11.7892 14.6203 11.9073 14.5925 12.0165 14.5426C12.1258 14.4928 12.2241 14.4219 12.306 14.334C12.3879 14.2462 12.4516 14.143 12.4936 14.0305C12.5356 13.918 12.5551 13.7983 12.5508 13.6783C12.5466 13.5583 12.5187 13.4403 12.4689 13.331C12.419 13.2218 12.3482 13.1234 12.2603 13.0416C11.4322 12.2701 10.4011 10.9648 9.40111 9.33594L11.3901 7.34694C11.4888 7.56632 11.6003 7.79118 11.7247 8.01421C12.3627 9.16044 13.4065 10.5023 14.699 11.7948C15.9897 13.0873 17.3315 14.1311 18.4796 14.7673C18.7026 14.8916 18.9256 15.005 19.1468 15.1019L10.2603 23.9902C10.2186 23.9866 10.1771 23.9805 10.136 23.9719C9.88557 23.9226 9.5108 23.7818 9.02452 23.5112C8.06658 22.9774 6.85636 22.0487 5.6498 20.8422C4.44324 19.6356 3.51455 18.429 2.98074 17.4674C2.712 16.983 2.56941 16.6082 2.52188 16.3578C2.5133 16.3167 2.5072 16.2752 2.5036 16.2335L8.07572 10.6632ZM13.3224 7.12574C13.1332 6.80368 12.9852 6.45912 12.8819 6.10016C13.1287 6.16049 13.4742 6.30126 13.9093 6.54074C14.8672 7.07455 16.0774 8.00324 17.284 9.2098C18.1066 10.0325 18.8013 10.8569 19.3278 11.6046C18.6735 11.5598 18.0163 11.6159 17.379 11.771C16.8968 11.3695 16.4332 10.9461 15.9897 10.5023C14.7831 9.29573 13.8563 8.08551 13.3243 7.12574H13.3224Z',
  },
} as const;

const customSizemap = {
  '2xs': 'text-2xs',
  xs: 'text-xs',
  sm: 'text-sm',
  lg: 'text-base',
  xl: 'text-xl',
  '2xl': 'text-base',
  '1x': 'text-2x',
  '2x': 'text-3x',
  '3x': 'text-4x',
  '4x': 'text-5x',
  '5x': 'text-5x',
  '6x': 'text-7x',
  '7x': 'text-8x',
  '8x': 'text-9x',
  '9x': 'text-10x',
  '10x': 'text-11x',
};

const Icon = ({ name, size, colorClass, className = '' }: Props) => {
  const isCustomIcon = !!CUSTOM_ICONS[name as CustomIconName];

  if (isCustomIcon) {
    const { viewBox, path } = CUSTOM_ICONS[name as CustomIconName];
    const iconSizeClass = size ? customSizemap[size] : '';

    return (
      <svg
        className={`box-content inline-block h-[1em] overflow-visible align-[-0.125em] ${iconSizeClass} ${className} ${colorClass}`}
        role="img"
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={path} fill="currentColor" />
      </svg>
    );
  }

  return (
    <FontAwesomeIcon
      className={`${colorClass} ${className}`}
      icon={ICON_NAMES[name as GenericIconName] as IconProp}
      size={size}
    />
  );
};

export default Icon;
